// New accounts write the value EXPLICITLY at signup (Easy unless they pick Advanced), so "never
// chose" only ever describes someone who predates the setting — and for them, today's behaviour is
// the safe answer.
import { create } from "zustand";
import { api } from "../api";
import { reportSwallowed } from "../lib/reportSwallowed";

export type UiMode = "easy" | "advanced";
export type LayerDetail = "easy" | "medium" | "all";

const MODE_KEY = "sm_ui_mode";
const DETAIL_KEY = "sm_layer_detail";

const isMode = (v: unknown): v is UiMode => v === "easy" || v === "advanced";
const isDetail = (v: unknown): v is LayerDetail =>
  v === "easy" || v === "medium" || v === "all";

function load<T>(key: string, ok: (v: unknown) => v is T, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return ok(raw) ? raw : fallback;
  } catch {
    return fallback;
  }
}

interface UiModeState {
  mode: UiMode;
  layerDetail: LayerDetail;
  /*
   *
   * The gate is the thing that decides whether we risk a PATCH the server might refuse, so it has
   * to be resettable and inspectable — a hidden module flag survives between tests and made the
   * send-gate test pass for the wrong reason, which is precisely the failure mode a gate must not
   * have.
   */
  serverHasMode: boolean;
  serverHasDetail: boolean;
  setMode: (m: UiMode) => void;
  setLayerDetail: (d: LayerDetail) => void;
  hydrateFromServer: (
    prefs: Record<string, unknown> | null | undefined,
  ) => void;
}

export const useUiMode = create<UiModeState>((set, get) => ({
  mode: load(MODE_KEY, isMode, "advanced"),
  layerDetail: load(DETAIL_KEY, isDetail, "all"),
  serverHasMode: false,
  serverHasDetail: false,

  setMode: (mode) => {
    set({ mode });
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch {
      /* private mode — degrades to this session */
    }
    // A failed save leaves the local value in place rather than reverting under the user's cursor.
    if (get().serverHasMode)
      void api
        .updateSoilUnits({ ui_mode: mode })
        .catch((err) => reportSwallowed("ui mode preference", err));
  },

  setLayerDetail: (layerDetail) => {
    set({ layerDetail });
    try {
      localStorage.setItem(DETAIL_KEY, layerDetail);
    } catch {
      /* as above */
    }
    if (get().serverHasDetail)
      void api
        .updateSoilUnits({ layer_detail: layerDetail })
        .catch((err) => reportSwallowed("layer detail preference", err));
  },

  hydrateFromServer: (prefs) => {
    if (!prefs || typeof prefs !== "object") return;
    // PRESENCE, not value — `null` means "the server knows this key and the user never chose" which
    // is different from a service that predates the key. Presence is also what opens the send-gate
    // so a key we have never seen served is never sent.
    if ("ui_mode" in prefs) {
      set({ serverHasMode: true });
      // `null` = never chose: keep whatever this browser holds rather than discarding a choice made
      // before the key existed.
      if (isMode(prefs.ui_mode)) set({ mode: prefs.ui_mode });
    }
    if ("layer_detail" in prefs) {
      set({ serverHasDetail: true });
      if (isDetail(prefs.layer_detail))
        set({ layerDetail: prefs.layer_detail });
    }
  },
}));

/*
 *
 * Easy Mode pins this to `easy` no matter what the 3-way toggle last held, and because the toggle
 * is hidden in Easy Mode the stored value is left untouched — so switching back to Advanced
 * restores the detail the user had chosen rather than resetting them to All.
 */
export function effectiveDetail(
  mode: UiMode,
  layerDetail: LayerDetail,
): LayerDetail {
  return mode === "easy" ? "easy" : layerDetail;
}

/* * Easy Mode hides the customization / full-control entry points for maps and Rx. */
export function showsAdvancedControls(mode: UiMode): boolean {
  return mode !== "easy";
}
